import { defineStore } from 'pinia';
import { adminApi } from '@/api/admin';
import type { UpdateMetadataRequest } from '@/api/types';
import { useCatalogStore } from '@/stores/catalog';
import { useServicesStore } from '@/stores/services';

// Write-side wrapper for the three per-entity metadata endpoints. There is no per-entity GET
// — reads ride the catalog (Groups, Projects) and the services list (Services), so we don't
// hold cached metadata locally. On a successful save we trigger a refresh of the relevant
// store so views consuming `displayName`/`description`/`category` see the new values without
// the caller having to coordinate that themselves.
//
// Returns the same `{ ok: true } | { ok: false, error }` shape every other write action uses,
// so view components can wire the result identically.
export const useMetadataStore = defineStore('metadata', {
  state: () => ({}),
  actions: {
    async updateGroup(
      group: string,
      request: UpdateMetadataRequest,
    ): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.updateGroupMetadata(group, request);
        await useCatalogStore().load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },

    async updateProject(
      group: string,
      project: string,
      request: UpdateMetadataRequest,
    ): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.updateProjectMetadata(group, project, request);
        await useCatalogStore().load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },

    async updateService(
      name: string,
      request: UpdateMetadataRequest,
    ): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.updateServiceMetadata(name, request);
        await useServicesStore().load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
  },
});
